// src/components/TaskAllocationModal.jsx
import React, { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../../styles/TaskAllocationModel.css";
import { FETCH_EMPLOYEES_REQUEST } from "../../../store/Actions/Actions";
import { useDispatch, useSelector } from "react-redux";
import Axios from "../../../utils/Axios";
import {
  TASK_EDIT_FOR_EMPLOYEE,
  TASK_FOR_EMPLOYEE,
  DELETE_TASK_ALLOCATION,
} from "../../../config/API_Collection";
import Toast from "../../../utils/Toast";
import SearchableDropdown from "../../../components/DropdownSearch";
import { ConfirmationBox } from "../../../components/ConformationBox";

/* ---------- rows reducer ---------- */
function rowsReducer(state, action) {
  switch (action.type) {
    case "INIT":
      return Array.isArray(action.payload) ? action.payload : [];
    case "PREPEND":
      return [action.payload, ...state];
    case "PATCH_ROW": {
      const { id, patch } = action;
      return state.map((r) => (r.id === id || r.assignment_id === id ? { ...r, ...patch } : r));
    }
    case "DELETE_ROW": {
      const { id } = action;
      return state.filter((r) => r.id !== id && r.assignment_id !== id);
    }
    default:
      return state;
  }
}

export default function TaskAllocationModal({
  setTaskAllocationModel,
  taskAssign = {},
  userOptions = [],
  onAssign,
  update = false,
  setUpdate = () => {},
}) {
  const task = useMemo(() => taskAssign || {}, [taskAssign]);
  
  const initialRows = useMemo(
    () => (Array.isArray(task?.assignments) ? task.assignments : []),
    [task]
  );

  const loginState = useSelector((s) => s?.login);
  const actions = loginState?.actions || null;
  const canAddTask = actions?.tasks?.add_task === true;
  const canDeleteTask = actions?.tasks?.delete_task === true;
  const canEditTask = actions?.tasks?.edit_task === true;

  const employees = useSelector((_) => _?.employees?.employees);
  const DISPATCH = useDispatch();

  useEffect(() => {
    DISPATCH({ type: FETCH_EMPLOYEES_REQUEST, payload: null });
  }, [DISPATCH]);

  const [rows, dispatchRows] = useReducer(rowsReducer, []);
  useEffect(() => {
    dispatchRows({ type: "INIT", payload: initialRows });
  }, [initialRows]);

  // Maximum height for scrollable table area
  const maxTableHeight = 300; // pixels

  // ===== Toast =====
  const [toast, setToast] = useState({ message: "", type: "", triggerId: 0 });
  const showToast = (message, type) =>
    setToast({ message, type, triggerId: Date.now() });

  // ===== Visible loaders only on Save buttons =====
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const addingRef = useRef(false);
  const editingRef = useRef(false);

  // abort controllers
  const addAbort = useRef(null);
  const editAbort = useRef(null);
  useEffect(() => {
    return () => {
      addAbort.current?.abort?.();
      editAbort.current?.abort?.();
    };
  }, []);

  const [showAddRow, setShowAddRow] = useState(false);
  const [newRow, setNewRow] = useState({
    employee_id: "",
    first_name: "",
    selectedValue: "", // stores the display label from dropdown
    allocated_rate: "",
    allocated_hours: "",
  });

  // Build employee labels for SearchableDropdown
  const employeeLabels = useMemo(
    () =>
      (employees || []).map((e) =>
        [e?.first_name, e?.last_name].filter(Boolean).join(" ")
      ),
    [employees]
  );

  // ===== Inline edit state =====
  const [editIndex, setEditIndex] = useState(null);
  const [editRow, setEditRow] = useState({
    id: null,
    employee_id: "",
    first_name: "",
    allocated_rate: "",
    allocated_hours: "",
    worked_hours: 0,
    assignment_id: null,
  });

  // ===== Dropdown state =====
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdownIndex(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = (index) => {
    setOpenDropdownIndex(openDropdownIndex === index ? null : index);
  };

  // ===== Delete confirmation state =====
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ===== Totals =====
  const totals = useMemo(() => {
    let totalExpectedHours = 0;
    let totalExpectedDollars = 0;
    let totalWorkedHours = 0;
    let totalWorkedDollars = 0;

    for (const r of rows || []) {
      const rate =
        Number.parseFloat(r?.allocated_rate ?? r?.allocatedRate ?? 0) || 0;
      const allocH =
        Number.parseFloat(r?.allocated_hours ?? r?.allocatedHours ?? 0) || 0;
      const workH = Number.parseFloat(r?.worked_hours ?? 0) || 0;

      totalExpectedHours += allocH;
      totalExpectedDollars += rate * allocH;
      totalWorkedHours += workH;
      totalWorkedDollars += rate * workH;
    }
    return {
      totalExpectedHours,
      totalExpectedDollars,
      totalWorkedHours,
      totalWorkedDollars,
    };
  }, [rows]);

  const fmt2 = (n) =>
    Number.isFinite(n)
      ? n.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "0.00";
  const fmt1 = (n) =>
    Number.isFinite(n)
      ? n.toLocaleString(undefined, {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        })
      : "0.0";

  const expectedDollars = useMemo(() => {
    const rate = parseFloat(newRow.allocated_rate || 0);
    const hrs = parseFloat(newRow.allocated_hours || 0);
    if (Number.isNaN(rate) || Number.isNaN(hrs)) return "";
    const v = rate * hrs;
    return Number.isFinite(v) ? v.toFixed(2) : "";
  }, [newRow.allocated_rate, newRow.allocated_hours]);

  // ===== Add form handlers =====
  const handleOpenForm = () => setShowAddRow(true);
  const handleCloseForm = () => {
    setShowAddRow(false);
    setNewRow({
      employee_id: "",
      first_name: "",
      selectedValue: "",
      allocated_rate: "",
      allocated_hours: "",
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewRow((s) => ({ ...s, [name]: value }));
  };

  // New: handle SearchableDropdown selection for employee
  const handleSelectEmployee = (label) => {
    const emp = (employees || []).find(
      (e) => [e?.first_name, e?.last_name].filter(Boolean).join(" ") === label
    );
    const fullName = label || "";
    const defaultRate = emp?.billable_rate ?? emp?.billableRate ?? "";
    setNewRow((s) => ({
      ...s,
      selectedValue: label,
      employee_id: emp?.employee_id ?? "",
      first_name: fullName,
      allocated_rate:
        defaultRate === null || typeof defaultRate === "undefined"
          ? ""
          : String(defaultRate),
    }));
  };

  // ===== SAVE (create) =====
  const handleSaveRow = async (e) => {
    e?.preventDefault?.();
    if (!newRow?.employee_id || addingRef.current || editingRef.current) return;
    addingRef.current = true;
    setAdding(true);

    const rate = Number.parseFloat(newRow?.allocated_rate ?? 0) || 0;
    const hours = Number.parseFloat(newRow?.allocated_hours ?? 0) || 0;

    const payload = {
      task_id: task?.task_id,
      employee_id: newRow.employee_id,
      allocated_rate: rate,
      allocated_hours: hours,
    };

    const ac = new AbortController();
    addAbort.current = ac;

    try {
      const res = await Axios.post(TASK_FOR_EMPLOYEE, payload, {
        signal: ac.signal,
      });
      if (res?.status >= 200 && res?.status < 300) {
        const apiItem = res?.data?.data || res?.data?.result || res?.data || {};
        const realId =
          apiItem?.id ?? apiItem?.assignment_id ?? apiItem?.employee_task_id;

        const finalRow = {
          id: realId,
          first_name: apiItem?.first_name ?? newRow.first_name,
          employee_id: apiItem?.employee_id ?? newRow.employee_id,
          allocated_rate: apiItem?.allocated_rate ?? rate,
          allocated_hours: apiItem?.allocated_hours ?? hours,
          worked_hours: apiItem?.worked_hours ?? 0,
          worked_cost: apiItem?.worked_cost ?? 0,
          team: apiItem?.team ?? undefined,
        };

        dispatchRows({ type: "PREPEND", payload: finalRow });
        showToast("Task allocation successful!", "success");
        handleCloseForm();
        setUpdate(!update);
      } else {
        showToast("Failed to allocate task. Please try again.", "failed");
      }
    } catch (err) {
      const apiMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        (Array.isArray(err?.response?.data?.errors) &&
          err.response.data.errors.join(", ")) ||
        err?.message ||
        "Something went wrong while allocating the task.";
      console.error("Task allocation error:", err);
      showToast(apiMsg, "failed");
    } finally {
      setAdding(false);
      addingRef.current = false;
      addAbort.current = null;
    }
  };

  // ===== Inline EDIT =====
  const beginEdit = (rowIndex) => {
    const r = rows[rowIndex];
    if (!r) return;
    setShowAddRow(false);
    setEditIndex(rowIndex);
    setEditRow({
      id: r.id ?? r.assignment_id ?? null,
      employee_id: r.employee_id ?? "",
      first_name: r.first_name ?? "",
      allocated_rate: String(r.allocated_rate ?? r.allocatedRate ?? ""),
      allocated_hours: String(r.allocated_hours ?? r.allocatedHours ?? ""),
      worked_hours: r.worked_hours ?? 0,
      assignment_id: r.assignment_id ?? r.id ?? null,
    });
  };

  const cancelEdit = () => {
    setEditIndex(null);
    setEditRow({
      id: null,
      employee_id: "",
      first_name: "",
      allocated_rate: "",
      allocated_hours: "",
      worked_hours: 0,
      assignment_id: null,
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditRow((s) => ({ ...s, [name]: value }));
  };

  const saveEdit = async () => {
    if (editIndex == null || editingRef.current || addingRef.current) return;
    editingRef.current = true;
    setEditing(true);

    const rate = Number.parseFloat(editRow?.allocated_rate ?? 0) || 0;
    const hours = Number.parseFloat(editRow?.allocated_hours ?? 0) || 0;

    const current = rows[editIndex] || {};
    const assignmentId =
      editRow.id ??
      editRow.assignment_id ??
      current.assignment_id ??
      current.id;

    const employeeId = editRow.employee_id ?? current.employee_id;

    const payload = {
      employee_id: employeeId,
      task_assignment_id: assignmentId,
      allocated_hours: hours,
      allocated_rate: rate,
    };

    console.log('Editing task allocation:', payload);

    const ac = new AbortController();
    editAbort.current = ac;

    try {
      const res = await Axios.post(TASK_EDIT_FOR_EMPLOYEE, payload, {
        signal: ac.signal,
      });
      if (res?.status >= 200 && res?.status < 300) {
        dispatchRows({
          type: "PATCH_ROW",
          id: assignmentId,
          patch: {
            allocated_rate: rate,
            allocated_hours: hours,
            allocatedRate: rate,
            allocatedHours: hours,
          },
        });
        showToast("Task allocation updated successfully!", "success");
        cancelEdit();
        setUpdate(!update);
      } else {
        showToast(
          "Failed to update task allocation. Please try again.",
          "failed"
        );
      }
    } catch (err) {
      const apiMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        (Array.isArray(err?.response?.data?.errors) &&
          err.response.data.errors.join(", ")) ||
        err?.message ||
        "Something went wrong while updating the task allocation.";
      console.error("Task update error:", err);
      console.error("Error response:", err?.response?.data);
      showToast(apiMsg, "failed");
    } finally {
      setEditing(false);
      editingRef.current = false;
      editAbort.current = null;
    }
  };

  const editExpectedDollars = useMemo(() => {
    const rate = parseFloat(editRow.allocated_rate || 0);
    const hrs = parseFloat(editRow.allocated_hours || 0);
    if (Number.isNaN(rate) || Number.isNaN(hrs)) return "";
    const v = rate * hrs;
    return Number.isFinite(v) ? v.toFixed(2) : "";
  }, [editRow.allocated_rate, editRow.allocated_hours]);

  // ===== DELETE task allocation =====
  const handleDeleteClick = (taskData) => {
    setTaskToDelete(taskData);
    setShowDeleteConfirm(true);
    setOpenDropdownIndex(null);
  };

  const handleDeleteConfirm = async () => {
    if (!taskToDelete || deleting) return;
    setDeleting(true);

    const assignmentId = taskToDelete.id ?? taskToDelete.assignment_id;
    
    console.log('Deleting task allocation:', { taskToDelete, assignmentId });

    try {
      const res = await Axios.post(DELETE_TASK_ALLOCATION, {
        task_assignment_id: assignmentId,
      });

      if (res?.status >= 200 && res?.status < 300) {
        dispatchRows({
          type: "DELETE_ROW",
          id: assignmentId,
        });
        showToast("Task allocation deleted successfully!", "success");
        setUpdate(!update);
      } else {
        showToast("Failed to delete task allocation. Please try again.", "failed");
      }
    } catch (err) {
      const apiMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        (Array.isArray(err?.response?.data?.errors) &&
          err.response.data.errors.join(", ")) ||
        err?.message ||
        "Something went wrong while deleting the task allocation.";
      console.error("Task delete error:", err);
      console.error("Error response:", err?.response?.data);
      showToast(apiMsg, "failed");
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
      setTaskToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setTaskToDelete(null);
  };

  return (
    <>
      <Toast
        message={toast.message}
        type={toast.type}
        triggerId={toast.triggerId}
      />

      {/* ---- Critical: ensure dropdowns can overflow and appear above modal ---- */}
      <style>{`
        /* Let popups overflow the modal box */
        .modal,
        .modal-dialog,
        .modal-content {
          overflow: visible !important;
        }

        /* High stacking order for our searchable dropdown menu */
        .searchable-dropdown-menu {
          position: absolute !important;
          z-index: 2000 !important; /* higher than Bootstrap modal (1055) */
        }

        /* Actions dropdown styling */
        .actions-dropdown-menu {
          position: absolute !important;
          right: 0;
          top: 100%;
          z-index: 2100 !important;
          min-width: 150px;
          background: white;
          border: 1px solid #dee2e6;
          border-radius: 0.375rem;
          box-shadow: 0 0.5rem 1rem rgba(0,0,0,0.15);
          margin-top: 0.25rem;
          padding: 0.5rem 0;
        }

        .actions-dropdown-menu .dropdown-item {
          padding: 0.5rem 1rem;
          color: #212529;
          text-decoration: none;
          display: block;
          border: none;
          background: transparent;
          width: 100%;
          text-align: left;
          cursor: pointer;
        }

        .actions-dropdown-menu .dropdown-item:hover {
          background-color: #f8f9fa;
        }

        .actions-dropdown-container {
          position: relative;
        }

        /* A couple of local input helpers */
        .no-spinners::-webkit-outer-spin-button,
        .no-spinners::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        .no-spinners { -moz-appearance: textfield; }

        .cell-input { max-width: 110px; margin: 0 auto; text-align: center; }
        .cell-box { max-width: 130px; margin: 0 auto; text-align: center; }

        .btn-modal-x {
          position: absolute;
          top: 0;
          right: 0;
          border: 0;
          background: transparent;
          color: #6c757d;
          line-height: 1;
          cursor: pointer;
          border-bottom-left-radius: .5rem;
        }
        .btn-modal-x:hover { background: #f1f3f5; }
      `}</style>

      <Modal
        show
        size="xl"
        centered
        backdrop="static"
        onHide={() => setTaskAllocationModel(false)}
      >
        {/* Delete Confirmation Dialog (cover entire modal) */}
        {showDeleteConfirm && (
          <ConfirmationBox
            title="Are you sure you want to delete this task allocation?"
            onConfirm={handleDeleteConfirm}
            onCancel={handleDeleteCancel}
            setConformBox={setShowDeleteConfirm}
          />
        )}
        <Modal.Header
          className="border-0 px-4 py-4 justify-content-between align-items-center timesheet-y"
          style={{ position: "relative" }}
        >
          <div className="d-flex justify-content-between align-items-center">
            <span className="ta-title">
              <h4>Task Allocation - </h4>
            </span>
            <span className="ta-subtitle text-end">
              T{String(task?.task_id ?? "").padStart(3, "0")}-
              {`${task?.task_name}`}
            </span>
          </div>

          <button
            type="button"
            className="btn-modal-x p-3 rounded m-2"
            aria-label="Close"
            title="Close"
            onClick={() => setTaskAllocationModel(false)}
          >
            X
          </button>
        </Modal.Header>

        <div className="ta-divider" />

        <Modal.Body className="px-4 pb-0 timesheet-body timesheet-y">
          <div className="card ta-card">
            <div className="card-body p-3">
              {/* Header row */}
              <div className="row gx-3 gy-2 ta-head align-items-center">
                <div className="col-12 col-lg-3 fw-semibold">Allocated Users</div>
                <div className="col-6 col-lg-1 fw-semibold text-lg-center">Rate</div>
                <div className="col-6 col-lg-1 fw-semibold text-lg-center">Expected (hrs)</div>
                <div className="col-6 col-lg-2 fw-semibold text-lg-center">Expected ($)</div>
                <div className="col-6 col-lg-2 fw-semibold text-lg-center">Worked (hrs)</div>
                <div className="col-6 col-lg-2 fw-semibold text-lg-center">Worked ($)</div>
                <div className="col-6 col-lg-1 fw-semibold text-lg-end">Actions</div>
              </div>

              {/* Rows (scrollable) */}
              <div 
                className="ta-scrollable-rows" 
                style={{ 
                  maxHeight: `${maxTableHeight}px`, 
                  overflowY: rows?.length > 4 ? 'auto' : 'visible',
                  overflowX: 'visible'
                }}
              >
              {rows?.length > 0 ? (
                rows.map((t, idx) => {
                  const rowIndex = idx;
                  const isEditing = editIndex === rowIndex;

                  if (isEditing) {
                    return (
                      <div
                        key={`edit-${t.id ?? rowIndex}`}
                        className="row gx-3 gy-2 ta-row align-items-center mt-2"
                      >
                        <div className="col-12 col-lg-3">
                          <div className="ta-user-name">
                            {editRow?.first_name || t?.first_name || ""}
                          </div>
                          <div className="ta-user-role">{t?.team}</div>
                        </div>

                        <div className="col-6 col-lg-1 text-lg-center">
                          <Form.Control
                            size="sm"
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="Rate"
                            name="allocated_rate"
                            value={editRow.allocated_rate}
                            onChange={handleEditChange}
                            className="cell-input no-spinners"
                            disabled={editing}
                          />
                        </div>

                        <div className="col-6 col-lg-1 text-lg-center">
                          <Form.Control
                            size="sm"
                            type="number"
                            step="0.1"
                            min="0"
                            placeholder="Hours"
                            name="allocated_hours"
                            value={editRow.allocated_hours}
                            onChange={handleEditChange}
                            className="cell-input no-spinners"
                            disabled={editing}
                          />
                        </div>

                        <div className="col-6 col-lg-2 text-lg-center">
                          <div className="form-control form-control-sm bg-light cell-box" readOnly>
                            {editExpectedDollars || "—"}
                          </div>
                        </div>

                        <div className="col-6 col-lg-2 text-lg-center">
                          {t?.worked_hours ?? "Missing"}
                        </div>

                        <div className="col-6 col-lg-2 text-lg-center">
                          {(
                            Number(editRow?.allocated_rate || t?.allocated_rate || 0) *
                            Number(t?.worked_hours || 0)
                          ).toFixed(2)}
                        </div>

                        <div className="col-6 col-lg-1 d-flex justify-content-end gap-2">
                          <Button size="sm" variant="success" onClick={saveEdit} disabled={editing}>
                            {editing ? <Spinner animation="border" size="sm" className="me-2" /> : <i className="bi bi-check-lg"></i>}
                          </Button>
                          <Button size="sm" variant="outline-secondary" onClick={cancelEdit} aria-label="Close edit row" title="Close edit row">
                            ×
                          </Button>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={t.id ?? `row-${idx}`} className="row gx-3 gy-2 ta-row align-items-center">
                      <div className="col-12 col-lg-3">
                        <div className="ta-user-name">{t?.first_name ?? ""}</div>
                        <div className="ta-user-role">{t?.team}</div>
                      </div>
                      <div className="col-6 col-lg-1 text-lg-center">
                        ${t?.allocated_rate ?? t?.allocatedRate ?? ""}
                      </div>
                      <div className="col-6 col-lg-1 text-lg-center">
                        {t?.allocated_hours ?? t?.allocatedHours ?? ""} hrs
                      </div>
                      <div className="col-6 col-lg-2 text-lg-center">
                        $
                        {(
                          Number(t?.allocated_rate ?? t?.allocatedRate ?? 0) *
                          Number(t?.allocated_hours ?? t?.allocatedHours ?? 0)
                        ).toFixed(2)}
                      </div>
                      <div className="col-6 col-lg-2 text-lg-center">
                        {t?.worked_hours ?? "Missing"} hrs
                      </div>
                      <div className="col-6 col-lg-2 text-lg-center">
                        $
                        {(
                          Number(t?.allocated_rate ?? t?.allocatedRate ?? 0) *
                          Number(t?.worked_hours || 0)
                        ).toFixed(2)}
                      </div>
                      <div className="col-6 col-lg-1 text-lg-end">
                        <div className="actions-dropdown-container" ref={openDropdownIndex === rowIndex ? dropdownRef : null}>
                          <button 
                            className="btn btn-sm" 
                            onClick={() => toggleDropdown(rowIndex)}
                            aria-expanded={openDropdownIndex === rowIndex}
                          >
                            <i className="bi bi-three-dots-vertical"></i>
                          </button>
                          {openDropdownIndex === rowIndex && (
                            <div className="actions-dropdown-menu">
                              {canEditTask && (
                                <button 
                                  className="dropdown-item" 
                                  type="button" 
                                  onClick={() => {
                                    beginEdit(rowIndex);
                                    setOpenDropdownIndex(null);
                                  }}
                                >
                                  Edit Task
                                </button>
                              )}
                              {canDeleteTask && (
                                <button 
                                  className="dropdown-item" 
                                  type="button"
                                  onClick={() => handleDeleteClick(t)}
                                >
                                  Delete Task
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="no-content-msg">
                  <span>No Allocations Available</span>
                </div>
              )}
              </div>

              {/* Add button row */}
              <div className="d-flex justify-content-between align-items-center mt-3">
                {canAddTask && !showAddRow && editIndex == null ? (
                  <div className="d-flex">
                    <Button
                      size="sm"
                      className="btn btn-primary"
                      onClick={handleOpenForm}
                      title="Add new task allocation"
                      disabled={adding || editing}
                    >
                      +
                    </Button>
                  </div>
                ) : (
                  <div />
                )}
              </div>

              {/* Add-row form */}
              {showAddRow && editIndex == null && (
                <form onSubmit={handleSaveRow}>
                  <div className="row gx-3 gy-2 ta-row align-items-center mt-4">
                    <div className="col-12 col-lg-3" style={{ position: "relative", zIndex: 1005 }}>
                      {/* 🔎 Searchable employee picker (will now sit above the modal) */}
                      <SearchableDropdown
                        label="User"
                        options={employeeLabels}
                        value={newRow.selectedValue}
                        onChange={handleSelectEmployee}
                        allowAll={false}
                        width="100%"
                      />
                    </div>

                    <div className="col-6 col-lg-1 text-lg-center">
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Rate"
                        name="allocated_rate"
                        value={newRow.allocated_rate}
                        onChange={handleChange}
                        disabled={adding || editing}
                        className="cell-input no-spinners"
                      />
                    </div>

                    <div className="col-6 col-lg-1 text-lg-center">
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.1"
                        min="0"
                        placeholder="Hours"
                        name="allocated_hours"
                        value={newRow.allocated_hours}
                        onChange={handleChange}
                        disabled={adding || editing}
                        className="cell-input no-spinners"
                      />
                    </div>

                    <div className="col-6 col-lg-2 text-lg-center">
                      <div className="form-control form-control-sm bg-light cell-box" readOnly>
                        ${expectedDollars || "—"}
                      </div>
                    </div>

                    <div className="col-6 col-lg-2 text-lg-center text-muted">—</div>
                    <div className="col-6 col-lg-2 text-lg-center text-muted">—</div>

                    <div className="col-6 col-lg-1 d-flex justify-content-end gap-2">
                      <Button
                        size="sm"
                        variant="success"
                        type="submit"
                        disabled={!newRow.employee_id || adding || editing}
                      >
                        {adding ? (
                          <Spinner animation="border" size="sm" className="me-2" />
                        ) : (
                          <i className="bi bi-check-lg"></i>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={handleCloseForm}
                        aria-label="Close form"
                        title="Close form"
                      >
                        ×
                      </Button>
                    </div>
                  </div>
                </form>
              )}

              {/* Totals */}
              <hr className="ta-hr my-4" />
              <div className="row py-4 gx-3 ta-total align-items-center">
                <div className="col-12 col-lg-3 text-muted">Total</div>
                <div className="col-6 col-lg-1"></div>
                <div className="col-6 col-lg-1 text-lg-center">
                  {fmt1(totals.totalExpectedHours)} hrs
                </div>
                <div className="col-6 col-lg-2 text-lg-center">
                  ${fmt2(totals.totalExpectedDollars)}
                </div>
                <div className="col-6 col-lg-2 text-lg-center">
                  {fmt1(totals.totalWorkedHours)} hrs
                </div>
                <div className="col-6 col-lg-1 text-lg-end ms-3">
                  ${fmt2(totals.totalWorkedDollars)}
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>

      </Modal>
    </>
  );
}
